import { useState } from "react";
import { Plus, GripVertical, Link2, TrendingDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LinkItemForm } from "@/components/shop/LinkItemForm";
import { toast } from "sonner";
import { api, useResource, fmtMoney, formatApiError } from "@/lib/api";
import { PageHeader, inputCls, btnPrimary, btnSecondary, EmptyState } from "@/components/shared";
import { ShopListCard } from "@/components/shop/ShopListCard";
import { ShopItemDialog } from "@/components/shop/ShopItemDialog";
import { DndBoard } from "@/components/Sortable";
import { useShopBoard } from "@/components/shop/useShopBoard";

const TABS = [
  { id: "tobuy", label: "To-Buy" },
  { id: "wishlist", label: "Wishlists" },
];

export default function Shopping() {
  const shops = useResource("/shop-lists");
  const todos = useResource("/todo-lists");
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "wishlist" ? "wishlist" : "tobuy");
  const [linkOpen, setLinkOpen] = useState(false);
  const [name, setName] = useState("");
  const [dialog, setDialog] = useState(null);
  const board = useShopBoard(shops);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const r = await api.post("/shop-lists", { name: name.trim(), kind: tab });
    shops.setData((d) => [...d, r.data]);
    setName("");
    toast.success("List created");
  };
  const removeList = async (id) => {
    await api.delete(`/shop-lists/${id}`);
    shops.setData((d) => d.filter((s) => s.id !== id));
  };
  const checkPrices = async () => {
    try {
      await api.post("/wishlists/check-prices");
      toast("Checking prices on your wishlist links. This page will refresh in a moment.");
      setTimeout(shops.reload, 20000);
    } catch (e) {
      toast(formatApiError(e));
    }
  };
  const visible = shops.data.filter((s) => s.kind === tab);
  const total = visible.flatMap((s) => s.items).filter((i) => !i.bought).reduce((s, i) => s + (i.price || 0), 0);

  return (
    <div data-testid="shopping-page">
      <PageHeader
        eyebrow="Plan your spending"
        title="Wishlists & To-Buy"
        subtitle="Keep what you need and what you dream of in one place, and link items to the to-do lists they belong to."
        action={
          <div className="bg-t-ink text-t-bg rounded-2xl px-6 py-4" data-testid="shopping-total-banner">
            <p className="text-[11px] uppercase tracking-[0.2em] text-t-line-strong">Still to spend</p>
            <p className="font-serif-display text-3xl font-semibold">{fmtMoney(total)}</p>
          </div>
        }
      />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 fade-up">
        <div className="flex bg-t-surface rounded-xl p-1 w-fit">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`shop-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-t-card shadow-sm" : "text-t-muted"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "wishlist" && (
          <button data-testid="check-prices-button" onClick={checkPrices} className={`${btnSecondary} md:ml-auto`}><TrendingDown className="h-4 w-4" /> Check prices</button>
        )}
        <button data-testid="add-from-link-button" onClick={() => setLinkOpen(true)} className={`${btnPrimary} ${tab === "wishlist" ? "" : "md:ml-auto"}`}><Link2 className="h-4 w-4" /> Add from link</button>
        <form onSubmit={create} className="flex gap-3 md:w-[420px]" data-testid="new-shop-list-form">
          <input data-testid="new-shop-list-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)}
            placeholder={tab === "wishlist" ? "New wishlist, e.g. Living Room Dreams" : "New to-buy list, e.g. Groceries"} />
          <button type="submit" data-testid="create-shop-list-button" className={btnPrimary}><Plus className="h-4 w-4" /> Add</button>
        </form>
      </div>
      {visible.length ? (
        <DndBoard {...board.handlers} overlay={board.dragItem && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-t-card border border-t-primary/50 shadow-xl text-sm font-medium cursor-grabbing" data-testid="shop-drag-overlay">
            <GripVertical className="h-4 w-4 text-t-muted" /> {board.dragItem.name}
          </div>
        )}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {visible.map((s) => (
              <ShopListCard key={s.id} list={s} todos={todos.data} onChange={shops.replace} onDelete={() => removeList(s.id)} highlight={board.overList === s.id}
                onAdd={() => setDialog({ list: s })} onEdit={(item) => setDialog({ list: s, item })} />
            ))}
          </div>
        </DndBoard>
      ) : (
        !shops.loading && <EmptyState testId="shop-lists-empty" title={`No ${tab === "wishlist" ? "wishlists" : "to-buy lists"} yet`} text="Create one above to start adding items." />
      )}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent aria-describedby={undefined} className="bg-t-bg border-t-line rounded-2xl sm:max-w-xl" data-testid="add-from-link-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-3xl font-semibold">Add from a link</DialogTitle></DialogHeader>
          {linkOpen && <LinkItemForm onCancel={() => setLinkOpen(false)} onDone={() => { setLinkOpen(false); setTab("wishlist"); shops.reload(); }} />}
          <p className="text-xs text-t-muted border-t border-t-line pt-4" data-testid="share-menu-tip">
            Tip: on Android, open this site in Chrome and choose "Add to Home screen". Then tap Share → Base and Balance from any shop to send links straight here.
          </p>
        </DialogContent>
      </Dialog>
      <ShopItemDialog state={dialog} todos={todos.data} shopLists={shops.data} onClose={() => setDialog(null)} onSaved={shops.replace} onMoved={shops.reload} />
    </div>
  );
}
